import {
  Activity,
  BarChart3,
  Boxes,
  Building2,
  CalendarDays,
  CreditCard,
  Dumbbell,
  Gauge,
  KeyRound,
  type LucideProps,
  Newspaper,
  QrCode,
  ScrollText,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Swords,
  Trophy,
  UserCog,
  UserPlus,
  Users,
  UsersRound,
  Wallet,
} from 'lucide-solid';
import type { Component } from 'solid-js';

export const NAV_GROUPS = [
  { key: 'shift', title: 'Смена' },
  { key: 'directions', title: 'Направления' },
  { key: 'catalog', title: 'Каталог и программы' },
  { key: 'reports', title: 'Финансы и отчёты' },
  { key: 'system', title: 'Система' },
] as const;

export type NavGroup = (typeof NAV_GROUPS)[number]['key'];

export interface NavItem {
  href: string;
  label: string;
  icon: Component<LucideProps>;
  group: NavGroup;
  keywords?: string[];
  comingSoon?: string;
  deskLabel?: string;
}

export const NAV_ITEMS: NavItem[] = [
  {
    href: '/dashboard',
    label: 'Главная',
    deskLabel: 'Смена',
    icon: Gauge,
    group: 'shift',
    keywords: ['Главная', 'Смена'],
  },
  { href: '/schedule', label: 'Шахматка', icon: CalendarDays, group: 'shift' },
  { href: '/bookings', label: 'Бронирования', icon: Activity, group: 'shift' },
  { href: '/clients', label: 'Клиенты', icon: Users, group: 'shift' },
  { href: '/guests', label: 'Гости', icon: UserPlus, group: 'shift' },
  { href: '/pos', label: 'Касса', icon: CreditCard, group: 'shift' },
  {
    href: '/access',
    label: 'Доступ и посещения',
    icon: KeyRound,
    group: 'shift',
    keywords: ['СКУД', 'турникет', 'проходы', 'Sigur'],
  },
  { href: '/moderation/reports', label: 'Жалобы', icon: ShieldAlert, group: 'shift' },
  { href: '/trainers', label: 'Тренеры', icon: Dumbbell, group: 'directions' },
  { href: '/group-sessions', label: 'Групповые', icon: UsersRound, group: 'directions' },
  { href: '/fitness', label: 'Фитнес', icon: Dumbbell, group: 'directions' },
  { href: '/tournaments', label: 'Турниры', icon: Trophy, group: 'directions' },
  { href: '/matchmaking', label: 'Открытые матчи', icon: Swords, group: 'directions' },
  { href: '/organizations', label: 'Организации', icon: Building2, group: 'directions' },
  { href: '/loyalty', label: 'Лояльность', icon: ShieldCheck, group: 'catalog' },
  { href: '/invite', label: 'Привлечение клиентов', icon: QrCode, group: 'catalog' },
  { href: '/news', label: 'Новости', icon: Newspaper, group: 'catalog' },
  { href: '/finance', label: 'Финансы', icon: Wallet, group: 'reports' },
  { href: '/analytics', label: 'Аналитика', icon: BarChart3, group: 'reports' },
  { href: '/activity', label: 'Журнал', icon: ScrollText, group: 'reports' },
  { href: '/staff', label: 'Сотрудники', icon: UserCog, group: 'system' },
  { href: '/subscription', label: 'Подписка', icon: CreditCard, group: 'system' },
  { href: '/settings', label: 'Настройки', icon: Settings, group: 'system' },
  { href: '/platform', label: 'Платформа', icon: Boxes, group: 'system' },
];

export const NAVIGABLE_ITEMS: NavItem[] = NAV_ITEMS.filter((i) => i.comingSoon === undefined);

const OWNER_ROLES = ['super_admin', 'club_owner', 'admin'] as const;
const DESK_ROLES = ['manager', 'cashier'] as const;

export const isDeskStaff = (roles: readonly string[]): boolean =>
  roles.some((r) => (DESK_ROLES as readonly string[]).includes(r)) &&
  !roles.some((r) => (OWNER_ROLES as readonly string[]).includes(r));

export const navLabelOf = (item: NavItem, roles: readonly string[]): string =>
  item.deskLabel !== undefined && isDeskStaff(roles) ? item.deskLabel : item.label;
